// The four stat cards: live runs, open questions, model spend and broker calls. A figure the
// host does not report yet shows a dash rather than a made-up zero.
import { ChatCircleDots, Coins, Lightning, ShieldCheck } from "@phosphor-icons/react";

import { ago } from "../pages/format";
import { MetricCard } from "../ui/Card";
import type { BrokerCall, ModelUsage, PendingQuestion } from "./api";
import { deniedCount, hourly, lastDay } from "./broker";
import { usd } from "./format";
import { Sparkline } from "./lazyCharts";
import type { RunCounts } from "./runs";
import { useT } from "../i18n";

export function Metrics({
  counts,
  questions,
  usage,
  calls,
  now,
}: {
  counts: RunCounts | undefined;
  questions: PendingQuestion[];
  usage: ModelUsage | undefined;
  calls: BrokerCall[] | undefined;
  /** When the page was rendered first; "24 h" and "ago" count back from it. */
  now: number;
}) {
  const t = useT().dashboard;
  const oldest = Math.min(...questions.map((q) => q.asked_at));
  const recent = calls ? lastDay(calls, now) : undefined;
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:col-span-12 xl:grid-cols-4">
      <MetricCard
        title={t.activeRuns}
        metric={counts ? counts.working + counts.waiting : "—"}
        icon={<Lightning weight="duotone" />}
        iconClassName="bg-blue-lighter text-blue-dark"
        info={counts ? t.runsInfo(counts.working, counts.waiting) : t.loadingRuns}
      />
      <MetricCard
        title={t.openQuestions}
        metric={questions.length}
        icon={<ChatCircleDots weight="duotone" />}
        iconClassName="bg-orange-lighter text-orange-dark"
        info={questions.length > 0 ? t.oldestAsked(ago(oldest, now)) : t.nothingWaiting}
      />
      <MetricCard
        title={t.spend30}
        metric={usage ? usd(usage.total_usd) : "—"}
        icon={<Coins weight="duotone" />}
        iconClassName="bg-primary-lighter text-primary"
        chart={usage && usage.by_day.length > 1 ? <Sparkline data={usage.by_day.map((d) => d.usd)} /> : undefined}
        info={usage ? t.spendInfo(usd(usage.today_usd), usage.runs) : t.notReported}
      />
      <MetricCard
        title={t.calls24}
        metric={recent ? `${recent.length}${calls && recent.length === calls.length && calls.length >= 20 ? "+" : ""}` : "—"}
        icon={<ShieldCheck weight="duotone" />}
        iconClassName="bg-secondary-lighter text-secondary-dark"
        chart={recent && recent.length > 0 ? <Sparkline data={hourly(recent, now)} color="c2" /> : undefined}
        info={recent ? t.deniedCount(deniedCount(recent)) : t.notReported}
      />
    </div>
  );
}
